package com.vidaensupalabra.vsp.ventanas

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

import com.vidaensupalabra.vsp.utils.getFromLocalStorage
import com.vidaensupalabra.vsp.utils.saveToLocalStorage
import com.vidaensupalabra.vsp.datos.YouTubeRepository
import com.vidaensupalabra.vsp.ui.theme.VspBase
import com.vidaensupalabra.vsp.ui.theme.VspMarco
import com.vidaensupalabra.vsp.ui.theme.VspMarcoTransparente
import com.vidaensupalabra.vsp.ui.theme.VspMarcoTransparente50
import com.vidaensupalabra.vsp.ui.theme.White

// Constantes de Configuración Globales
private const val YOUTUBE_CHANNEL_ID = "UCSSDg4_om8o4rMaHTeT6KhQ"
private const val YOUTUBE_CHANNEL_URL = "https://www.youtube.com/@vidaensupalabra3623"
private const val STORY_CHURCH_URL = "https://story.church/"
private const val ACTS_29_URL = "https://www.acts29.com/network/latin-america/?lang=es"
private const val PILLAR_NETWORK_URL = "https://thepillarnetwork.com/"

private val HORARIOS_LIST = listOf(
    Triple("MIÉRCOLES", "Reunión de Oración", "6:30 PM"),
    Triple("DOMINGO", "Escuela Dominical", "8:30 AM"),
    Triple("DOMINGO", "Culto principal de adoración", "10:00 AM")
)

// Utilidad simple para reemplazar Spacer de Compose UI
@Composable
private fun VerticalSpacer(size: CSSNumeric) {
    Div(attrs = { style { height(size) } })
}

@Composable
fun HomeScreen() {
    var latestVideoId by remember { mutableStateOf(getFromLocalStorage("yt_latest_video_id")) }

    LaunchedEffect(Unit) {
        try {
            val fetchedId = YouTubeRepository.getLatestVideoId(YOUTUBE_CHANNEL_ID)
            if (fetchedId != null && fetchedId != latestVideoId) {
                latestVideoId = fetchedId
                if (fetchedId.isNotEmpty()) {
                    saveToLocalStorage("yt_latest_video_id", fetchedId)
                }
            }
            
            // Si falló la red (fetchedId == null) y no hay caché local (primera vez que abre la app)
            if (fetchedId == null && latestVideoId == null) {
                latestVideoId = "" // Quita el estado de carga y muestra un fallo elegante
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            println("WARN: Fallo asíncrono capturado en HomeScreen -> ${e.message}")
        }
    }

    HomeContent(latestVideoId = latestVideoId)
}

@Composable
fun HomeContent(latestVideoId: String?) {
    // Contenedor Principal (Reemplazo de Scaffold y Column global)
    Div(
        attrs = {
            style {
                backgroundColor(VspBase)
                width(100.percent)
                minHeight(100.vh)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                fontFamily("sans-serif")
                // El scroll nativo del body se encargará de desplazarse,
                // no necesitamos un modificador verticalScroll.
            }
        }
    ) {
        HomeTopAppBar()
        
        Div(attrs = {
            style {
                width(100.percent)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
            }
        }) {
            HeroSection()
            VerticalSpacer(8.px)
            Horarios()
            VerticalSpacer(16.px)
            LatestSermonSection(videoId = latestVideoId)
            VerticalSpacer(16.px)
            EquipoPastoral()
            VerticalSpacer(16.px)
            MinisteriosAsociados(
                STORY_CHURCH_URL,
                ACTS_29_URL,
                PILLAR_NETWORK_URL
            )
            VerticalSpacer(40.px) // Margen final inferior
        }
    }
}

@Composable
private fun HomeTopAppBar() {
    Header(attrs = {
        style {
            width(100.percent)
            height(90.px)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            backgroundColor(Color("transparent"))
        }
    }) {
        Img(src = "drawable/banner.png",
            alt = "Banner Iglesia",
            attrs = {
                style {
                    height(100.percent)
                    width(100.percent)
                    property("object-fit", "contain")
                    padding(8.px, 0.px)
                }
            }
        )
    }
}

@Composable
private fun HeroSection() {
    Div(attrs = {
        style {
            width(100.percent)
            minHeight(370.px)
            position(Position.Relative)
        }
    }) {
        BackgroundImage()
        TextoBienvenida()
    }
}

@Composable
fun BackgroundImage() {
    // Contenedor absoluto de la imagen de fondo y la superposición oscura
    Div(attrs = {
        style {
            position(Position.Absolute)
            top(0.px)
            left(0.px)
            width(100.percent)
            height(100.percent)
            backgroundColor(VspBase)
        }
    }) {
        Img(src = "drawable/1-scaled.png",
            alt = "Fondo de inicio",
            attrs = {
                style {
                    width(100.percent)
                    height(100.percent)
                    property("object-fit", "cover")
                }
            }
        )
        // Capa superpuesta (Overlay oscuro)
        Div(attrs = {
            style {
                position(Position.Absolute)
                top(0.px)
                left(0.px)
                width(100.percent)
                height(100.percent)
                backgroundColor(rgba(38, 50, 70, 0.7)) // VspBase con alpha 0.7
            }
        })
    }
}

@Composable
fun TextoBienvenida() {
    Div(attrs = {
        style {
            position(Position.Relative) // Para estar sobre el fondo
            width(100.percent)
            height(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            justifyContent(JustifyContent.SpaceEvenly)
            alignItems(AlignItems.Center)
            padding(16.px)
            boxSizing("border-box")
            textAlign("center")
        }
    }) {
        Span(attrs = { style { fontSize(22.px); fontWeight("bold"); color(White) } }) {
            Text("Para todo en la vida")
        }
        Span(attrs = { style { fontSize(28.px); fontWeight("bold"); color(Color("#BDBDBD")) } }) {
            Text("CRISTO ES SUFICIENTE!")
        }
        P(attrs = { style { fontSize(18.px); color(White); lineHeight("1.5") } }) {
            Text("Somos una iglesia cristiana de confesión bautista, ubicada en la ciudad de Riohacha (COL), trabajando para promover la vida que agrada a Dios, que es conforme a Su Palabra y cuyo fundamento es Cristo y Su glorioso evangelio.")
            Br()
            Br()
            Text("¡Estamos felices que te intereses por conocernos!")
        }
    }
}

@Composable
fun LatestSermonSection(videoId: String?) {
    Div(attrs = {
        style {
            width(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
        }
    }) {
        H2(attrs = {
            style {
                fontWeight("bold")
                fontSize(24.px)
                color(VspMarco)
                textAlign("center")
                margin(16.px, 0.px, 8.px, 0.px)
            }
        }) { Text("ÚLTIMO SERMÓN") }

        when {
            videoId == null -> {
                Div(attrs = {
                    style {
                        width(100.percent)
                        height(200.px)
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.Center)
                        alignItems(AlignItems.Center)
                        color(VspMarco)
                        fontWeight("bold")
                    }
                }) { Text("Cargando sermón...") }
            }
            videoId.isNotEmpty() -> {
                SermonVideoCard(videoId = videoId)
            }
        }

        VerticalSpacer(12.px)

        Button(attrs = {
            onClick { window.open(YOUTUBE_CHANNEL_URL, "_blank") }
            style {
                margin(16.px)
                padding(12.px, 24.px)
                backgroundColor(VspMarcoTransparente50)
                color(White)
                fontWeight("bold")
                border(0.px)
                borderRadius(8.px)
                cursor("pointer")
            }
        }) { Text("Ver más sermones") }
    }
}

@Composable
private fun SermonVideoCard(videoId: String) {
    var isPlaying by remember(videoId) { mutableStateOf(false) }
    
    Div(attrs = {
        onClick { isPlaying = true }
        style {
            property("width", "calc(100% - 32px)") // fillMaxWidth with padding horizontal
            margin(0.px, 16.px)
            property("aspect-ratio", "16 / 9")
            backgroundColor(VspMarcoTransparente50)
            borderRadius(16.px)
            property("box-shadow", "0px 4px 8px rgba(0,0,0,0.3)")
            position(Position.Relative)
            property("overflow", "hidden")
            cursor("pointer")
        }
    }) {
        YouTubeThumbnail(videoId = videoId)
        
        // Capa oscura semi-transparente sobre la miniatura
        Div(attrs = {
            style {
                position(Position.Absolute)
                top(0.px); left(0.px)
                width(100.percent); height(100.percent)
                backgroundColor(rgba(0, 0, 0, 0.4))
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
            }
        }) {
            // Botón de Play (usamos un carácter gigante o texto en lugar de SVG para mantenerlo nativo)
            Span(attrs = { style { fontSize(64.px); color(White) } }) { Text("▶") }
            VerticalSpacer(8.px)
            Span(attrs = { style { color(White); fontSize(16.px) } }) { Text("Toca para reproducir") }
        }
    }

    if (isPlaying) {
        SermonVideoDialog(videoId = videoId, onDismiss = { isPlaying = false })
    }
}

@Composable
private fun SermonVideoDialog(videoId: String, onDismiss: () -> Unit) {
    // Diálogo Modal nativo con Div de pantalla completa
    Div(attrs = {
        style {
            position(Position.Fixed)
            top(0.px); left(0.px)
            width(100.vw); height(100.vh)
            backgroundColor(rgba(0, 0, 0, 0.8))
            property("z-index", 1000)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
        }
    }) {
        Div(attrs = {
            style {
                width(90.percent)
                maxWidth(800.px)
                backgroundColor(VspBase)
                borderRadius(16.px)
                padding(16.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                boxSizing("border-box")
            }
        }) {
            Div(attrs = {
                style {
                    width(100.percent)
                    property("aspect-ratio", "16 / 9")
                    borderRadius(8.px)
                    property("overflow", "hidden")
                }
            }) {
                YouTubeVideoPlayer(videoId = videoId, autoplay = true)
            }
            
            VerticalSpacer(16.px)
            
            Button(attrs = {
                onClick { onDismiss() }
                style {
                    padding(10.px, 24.px)
                    backgroundColor(VspMarco)
                    color(White)
                    border(0.px)
                    borderRadius(8.px)
                    cursor("pointer")
                    fontSize(16.px)
                }
            }) { Text("Cerrar") }
        }
    }
}

// COMPONENTES RESTANTES TRADUCIDOS AL DOM (Pastores, Horarios, Ministerios)

@Composable
fun PastorProfile(imageSrc: String, title: String, isExpanded: Boolean, onExpand: () -> Unit) {
    Div(attrs = {
        onClick { onExpand() }
        style {
            padding(8.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            cursor("pointer")
        }
    }) {
        Img(src = imageSrc, alt = title, attrs = {
            style { width(118.px); height(118.px); borderRadius(50.percent); property("object-fit", "cover") }
        })
        Div(attrs = {
            style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); marginTop(8.px) }
        }) {
            Span(attrs = { style { color(VspMarco); fontSize(20.px); marginRight(4.px) } }) {
                Text(if (isExpanded) "▼" else "▲")
            }
            Span(attrs = { style { color(VspMarco); fontSize(18.px) } }) { Text(title) }
        }
    }
}

@Composable
fun PastorBioCard(title: String, content: String) {
    Div(attrs = {
        style {
            property("width", "calc(100% - 32px)")
            margin(8.px, 16.px)
            padding(16.px)
            backgroundColor(VspMarcoTransparente)
            borderRadius(8.px)
            property("box-shadow", "0px 4px 6px rgba(0,0,0,0.1)")
            boxSizing("border-box")
        }
    }) {
        H3(attrs = { style { color(VspMarco); fontSize(18.px); textAlign("center"); margin(0.px, 0.px, 8.px, 0.px) } }) { Text(title) }
        P(attrs = { style { color(White); fontSize(16.px); textAlign("center"); margin(0.px); lineHeight("1.4") } }) { Text(content) }
    }
}

@Composable
fun IconRow(facebookUrl: String, instagramUrl: String, twitterUrl: String) {
    Div(attrs = {
        style {
            width(100.percent)
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceEvenly)
            padding(16.px, 0.px)
        }
    }) {
        @Composable
        fun socialLink(url: String, iconName: String) {
            Div(attrs = {
                onClick { window.open(url, "_blank") }
                style { padding(8.px); backgroundColor(VspMarcoTransparente50); borderRadius(8.px); cursor("pointer"); color(White) }
            }) { Text(iconName) } // Usamos texto o podríamos inyectar Img con iconos sociales
        }
        
        socialLink(facebookUrl, "Facebook")
        socialLink(instagramUrl, "Instagram")
        socialLink(twitterUrl, "Twitter")
    }
}

@Composable
fun MinisteriosAsociados(storychurchUrl: String, acts29Url: String, pillarnetworkUrl: String) {
    Div(attrs = {
        style {
            width(100.percent)
            padding(16.px)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            boxSizing("border-box")
        }
    }) {
        H2(attrs = { style { fontSize(24.px); color(White); textAlign("center"); margin(0.px) } }) { Text("MINISTERIOS ASOCIADOS") }
        VerticalSpacer(16.px)

        @Composable
        fun linkImage(url: String, src: String, alt: String) {
            Img(src = src, alt = alt, attrs = {
                onClick { window.open(url, "_blank") }
                style {
                    width(100.percent)
                    maxWidth(300.px)
                    height(60.px)
                    property("object-fit", "contain")
                    cursor("pointer")
                    margin(0.px, 0.px, 18.px, 0.px)
                    // Equivalente web a colorFilter tint blanco:
                    property("filter", "brightness(0) invert(1)")
                }
            })
        }

        linkImage(storychurchUrl, "drawable/storychurk.png", "Storychurch")
        linkImage(acts29Url, "drawable/acts29.png", "Acts29")
        linkImage(pillarnetworkUrl, "drawable/pillarnetwork.png", "PillarNetwork")
    }
}

@Composable
fun EquipoPastoral() {
    var expandedAndres by remember { mutableStateOf(false) }

    H2(attrs = { style { fontSize(24.px); color(VspMarco); textAlign("center"); margin(0.px) } }) { Text("EQUIPO PASTORAL") }
    VerticalSpacer(16.px)

    Div(attrs = { style { width(100.percent); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
        Div(attrs = { style { width(100.percent); display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceEvenly) } }) {
            PastorProfile(
                imageSrc = "drawable/andrescuadroscaled.jpg",
                title = "Andrés Aguilar",
                isExpanded = expandedAndres
            ) {
                expandedAndres = !expandedAndres
            }
        }

        if (expandedAndres) {
            PastorBioCard(
                title = "Andrés Aguilar",
                content = "Licenciado en Estudios Teológicos del Seminario Reformado Latinoamericano. Pastor de ICVSP desde 2013, actualmente a tiempo completo. Casado con Yuli Fragozo y padre de Samuel y Abbie."
            )
            IconRow(
                "https://www.facebook.com/andresaguidav/",
                "https://www.instagram.com/andresaguidav/",
                "https://twitter.com/PrAndresAguilar"
            )
        }
    }
}

@Composable
fun Horarios() {
    H2(attrs = {
        style { fontSize(24.px); fontWeight("bold"); color(White); textAlign("center"); width(100.percent); margin(0.px) }
    }) { Text("NUESTROS HORARIOS") }

    VerticalSpacer(8.px)

    HORARIOS_LIST.forEach { (dia, actividad, hora) ->
        HorarioCardItem(dia, actividad, hora)
    }
}

@Composable
fun HorarioCardItem(dia: String, actividad: String, hora: String) {
    Div(attrs = {
        style {
            property("width", "calc(100% - 32px)")
            margin(4.px, 16.px)
            padding(16.px)
            backgroundColor(VspMarcoTransparente50)
            borderRadius(8.px)
            property("box-shadow", "0px 4px 6px rgba(0,0,0,0.1)")
            boxSizing("border-box")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
        }
    }) {
        Span(attrs = { style { fontSize(24.px); fontWeight("bold"); color(White); textAlign("center"); width(100.percent) } }) { Text(dia) }
        VerticalSpacer(4.px)
        Span(attrs = { style { fontSize(16.px); color(White); textAlign("center"); width(100.percent) } }) { Text(actividad) }
        VerticalSpacer(4.px)
        Span(attrs = { style { fontSize(16.px); color(White); textAlign("center"); width(100.percent) } }) { Text(hora) }
    }
}